import {
  AlertTriangle,
  Check,
  ChevronRight,
  CircleDot,
  Clock,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AgeRange,
  CountrySelect,
  Field,
  LanguagePicker,
  MultiChips,
  SingleChips,
} from "@/components/fields";
import { KeyboardHints } from "@/components/KeyboardHints";
import { ScaleSlider } from "@/components/ScaleSlider";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useSliderKeyboard } from "@/lib/keyboard";
import { Link, navigate, useQuery } from "@/lib/router";
import { cn } from "@/lib/utils";
import { GENDERS, LIMITS } from "@/shared/basics";
import type { Importance, ScaleValue } from "@/shared/scale";
import type { Answers, Basics, Group, Profile as ProfileT, Question } from "@/shared/types";

type SaveState = "clean" | "dirty" | "saving" | "saved" | "error";

interface Payload {
  profile: ProfileT;
  groups: Group[];
  questions: Question[];
  answers: Answers;
  settings: { minCompleteness: number; settleHours: number };
}

function relativeTime(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "now";
  const hours = Math.floor(diff / 3600_000);
  const minutes = Math.round((diff % 3600_000) / 60_000);
  if (hours >= 1) return `${hours}h ${minutes}m`;
  return `${Math.max(1, minutes)}m`;
}

const FIXED_SECTIONS = [
  { id: "basics", label: "About you", emoji: "🪪" },
  { id: "filters", label: "Who you'll meet", emoji: "🔭" },
  { id: "words", label: "Your words", emoji: "✍️" },
  { id: "sharing", label: "Public link", emoji: "🔗" },
];

export function Profile({ onProfileChange }: { onProfileChange: (p: ProfileT) => void }) {
  const query = useQuery();
  const [data, setData] = useState<Payload | null>(null);
  const [basics, setBasics] = useState<Basics | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>("clean");
  const [active, setActive] = useState("basics");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copied, setCopied] = useState(false);
  /**
   * Which slider areas are open, by group key. Everything starts closed: eleven
   * areas of ~130 questions is a page nobody can find their place in, and the
   * nav beside it is only a table of contents if the contents are foldable.
   */
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(() => new Set());

  const sectionRefs = useRef(new Map<string, HTMLElement>());

  // -- load -----------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await api.get<Payload>("/api/profile");
        if (cancelled) return;
        setData(body);
        setAnswers(body.answers);
        setBasics(extractBasics(body.profile));
        onProfileChange(body.profile);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- which section am I looking at ---------------------------------------
  useEffect(() => {
    if (loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-15% 0px -75% 0px" },
    );
    for (const el of sectionRefs.current.values()) observer.observe(el);
    return () => observer.disconnect();
  }, [loading]);

  // -- don't lose edits -----------------------------------------------------
  useEffect(() => {
    if (saveState !== "dirty") return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [saveState]);

  const patchBasics = useCallback((patch: Partial<Basics>) => {
    setBasics((b) => (b ? { ...b, ...patch } : b));
    setSaveState("dirty");
  }, []);

  const setAnswer = useCallback((questionId: string, value: ScaleValue) => {
    setAnswers((a) => ({
      ...a,
      [questionId]: { value, importance: a[questionId]?.importance ?? 0 },
    }));
    setSaveState("dirty");
  }, []);

  const setImportance = useCallback((questionId: string, importance: Importance) => {
    setAnswers((a) => {
      const existing = a[questionId];
      if (!existing) return a;
      return { ...a, [questionId]: { ...existing, importance } };
    });
    setSaveState("dirty");
  }, []);

  /**
   * First question of each area, by area key. Held in a ref so the toggle below
   * can read it without taking it as a dependency — see the note there.
   */
  const firstOfGroup = useRef(new Map<string, string>());
  /** Which areas are open, and how to select a question — both read, never watched. */
  const openGroupsRef = useRef<ReadonlySet<string>>(openGroups);
  openGroupsRef.current = openGroups;
  const selectRef = useRef<(questionId: string, opts?: { scroll?: boolean }) => void>(
    () => {},
  );

  /**
   * Both stable for the page's lifetime, so the memoised sections holding them
   * never re-render for a changed callback identity. That's why the toggle
   * reads its lookup through a ref rather than closing over the derived map:
   * the map changes whenever an answer does, and a toggle that changed with it
   * would re-render all eleven sections on every keystroke.
   */
  const toggleGroup = useCallback((groupKey: string) => {
    // Read the decision here rather than inside the updater: an updater runs
    // during the *next* render, far too late to tell the lines below which way
    // this toggle went.
    const opening = !openGroupsRef.current.has(groupKey);
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (opening) next.add(groupKey);
      else next.delete(groupKey);
      return next;
    });
    // Opening an area lands you on its first question, ready for ← / →.
    // Closing one leaves the selection alone; there's nothing to land on.
    if (!opening) return;
    const first = firstOfGroup.current.get(groupKey);
    if (first) selectRef.current(first);
  }, []);

  /** Jumping to a closed area from the nav has to open it, or the jump lands on a lid. */
  const openGroup = useCallback((groupKey: string) => {
    setOpenGroups((prev) => (prev.has(groupKey) ? prev : new Set(prev).add(groupKey)));
  }, []);

  const clearAnswer = useCallback((questionId: string) => {
    setAnswers((a) => {
      const next = { ...a };
      delete next[questionId];
      return next;
    });
    setSaveState("dirty");
  }, []);

  const save = useCallback(async () => {
    if (!basics) return;
    setSaveState("saving");
    try {
      await api.put<{ profile: ProfileT }>("/api/profile/basics", { basics });
      const body = await api.put<{ profile: ProfileT }>("/api/profile/answers", { answers });
      setData((d) => (d ? { ...d, profile: body.profile } : d));
      onProfileChange(body.profile);
      setSaveState("saved");
      setTimeout(() => setSaveState((s) => (s === "saved" ? "clean" : s)), 2500);
    } catch {
      setSaveState("error");
    }
  }, [basics, answers, onProfileChange]);

  const mutate = useCallback(
    async (fn: () => Promise<{ profile: ProfileT }>) => {
      const body = await fn();
      setData((d) => (d ? { ...d, profile: body.profile } : d));
      onProfileChange(body.profile);
    },
    [onProfileChange],
  );

  const questionsByGroup = useMemo(() => {
    const out = new Map<string, Question[]>();
    for (const q of data?.questions ?? []) {
      const list = out.get(q.groupId) ?? [];
      list.push(q);
      out.set(q.groupId, list);
    }
    return out;
  }, [data?.questions]);

  /**
   * Prompts by id, for the "matched against their answer to X" note on paired
   * questions. Built here rather than inside a section because a pair can span
   * two groups, and a section only ever sees its own questions.
   */
  const promptById = useMemo(() => {
    const out = new Map<string, string>();
    for (const q of data?.questions ?? []) out.set(q.id, q.prompt);
    return out;
  }, [data?.questions]);

  /**
   * Answered/total per group, computed once per change rather than by
   * re-scanning every group's questions inside the render of each nav row.
   */
  const groupCounts = useMemo(() => {
    const out = new Map<string, { done: number; total: number }>();
    for (const [groupId, qs] of questionsByGroup) {
      let done = 0;
      for (const q of qs) if (answers[q.id]) done++;
      out.set(groupId, { done, total: qs.length });
    }
    return out;
  }, [questionsByGroup, answers]);

  const groupProgress = useCallback(
    (groupId: string) => groupCounts.get(groupId) ?? { done: 0, total: 0 },
    [groupCounts],
  );

  /**
   * Questions in the order they're rendered, which is the order ↑/↓ walk.
   *
   * Closed areas are included: walking off the end of one area opens the next
   * rather than stopping dead, so the walk has to know about the rows that
   * aren't mounted yet. `ensureVisible` below is what mounts them.
   */
  const questionOrder = useMemo(() => {
    const out: string[] = [];
    for (const g of data?.groups ?? []) {
      for (const q of questionsByGroup.get(g.id) ?? []) out.push(q.id);
    }
    return out;
  }, [data?.groups, questionsByGroup]);

  /** Which area holds a question, for opening it on the way in. */
  const groupKeyOfQuestion = useMemo(() => {
    const byId = new Map((data?.groups ?? []).map((g) => [g.id, g.key]));
    const out = new Map<string, string>();
    for (const q of data?.questions ?? []) {
      const key = byId.get(q.groupId);
      if (key) out.set(q.id, key);
    }
    return out;
  }, [data?.groups, data?.questions]);

  /** The first question of each area, keyed the way the nav and toggle name it. */
  useMemo(() => {
    const out = new Map<string, string>();
    for (const g of data?.groups ?? []) {
      const first = questionsByGroup.get(g.id)?.[0]?.id;
      if (first) out.set(g.key, first);
    }
    firstOfGroup.current = out;
  }, [data?.groups, questionsByGroup]);

  const { activeId, select, register } = useSliderKeyboard({
    order: questionOrder,
    isAnswered: useCallback((qid: string) => Boolean(answers[qid]), [answers]),
    onImportance: setImportance,
    ensureVisible: useCallback(
      (qid: string) => {
        const key = groupKeyOfQuestion.get(qid);
        if (key) openGroup(key);
      },
      [groupKeyOfQuestion, openGroup],
    ),
  });

  selectRef.current = select;

  /**
   * Clicking or tabbing onto a row selects it — and focuses its slider, so
   * ← / → work straight away without a second click on the control itself.
   *
   * Never scrolls: the pointer is already on the row, and a page that moved
   * between pointerdown and pointerup would slide the thumb out from under the
   * finger that was reaching for it.
   */
  const activateRow = useCallback(
    (questionId: string) => select(questionId, { scroll: false }),
    [select],
  );

  // -- states ---------------------------------------------------------------
  if (loading) {
    return (
      <Shell wide>
        <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" /> Opening your profile…
        </div>
      </Shell>
    );
  }

  if (failed || !data || !basics) {
    return (
      <Shell>
        <div className="flex min-h-[65vh] flex-col justify-center py-16">
          <div className="grid size-12 place-items-center rounded-2xl border border-destructive/25 bg-destructive/10 text-destructive">
            <AlertTriangle className="size-6" />
          </div>
          <h1 className="mt-6 text-3xl font-semibold tracking-tight">You're not signed in.</h1>
          <p className="mt-4 max-w-lg text-[15.5px] leading-relaxed text-muted-foreground">
            Your session may have expired, or you're on a device that's never been signed in.
            Either way, a fresh link fixes it.
          </p>
          <div className="mt-8">
            <Button asChild className="rounded-full px-6">
              <Link href="/join">Send me a link</Link>
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  const { profile, groups, settings } = data;
    const publicUrl = `${window.location.origin}/u/${profile.slug}`;
  const welcome = query.get("welcome") === "1";

  return (
    <Shell
      wide
      nav={
        <>
          <span className="hidden text-[13px] text-muted-foreground xl:inline">
            {profile.email}
          </span>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="hidden rounded-full text-[13px] sm:inline-flex"
          >
            <Link href="/matches">Matches</Link>
          </Button>
          <SaveControls
            state={saveState}
            settleHours={settings.settleHours}
            liveAt={profile.liveAt}
            paused={profile.paused}
            isLive={profile.isLive}
            onSave={save}
          />
        </>
      }
    >
      <div className="py-10">
        <h1 className="text-3xl font-semibold tracking-[-0.025em] sm:text-4xl">
          {welcome ? `Hello${profile.name ? `, ${profile.name}` : ""}.` : "Your profile"}
        </h1>
        <p className="mt-3 max-w-2xl text-[15.5px] leading-relaxed text-muted-foreground">
          The top three sections are facts and filters. Everything below them is a slider from −3
          to +3 — every extra one you answer sharpens the match, and none of them is ever shown to
          anybody. Those areas start closed; open one to answer it.
        </p>

        {/* Status */}
        <div className="mt-7 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
          <div className="rounded-2xl border border-border/70 bg-card/50 p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between gap-4 text-[13.5px]">
              <span className="text-muted-foreground">
                {profile.answeredCount} of {profile.questionCount} questions answered
              </span>
              <span className="font-medium tabular-nums">
                {Math.round(profile.completeness * 100)}%
              </span>
            </div>
            <div className="relative mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-gradient-to-r from-brand to-brand-alt transition-[width] duration-500"
                style={{ width: `${Math.max(2, profile.completeness * 100)}%` }}
              />
              <div
                aria-hidden
                title="Enough to be matched"
                className="absolute top-0 h-full w-px bg-foreground/30"
                style={{ left: `${settings.minCompleteness * 100}%` }}
              />
            </div>
            <p className="mt-2.5 text-[12.5px] leading-relaxed text-muted-foreground">
              {profile.missing.length > 0 ? (
                <>
                  <span className="text-destructive">
                    {profile.missing.length} required field
                    {profile.missing.length === 1 ? "" : "s"}
                  </span>{" "}
                  still needed before you can be matched.
                </>
              ) : !profile.matchable ? (
                <>
                  Answer at least{" "}
                  <span className="text-brand">
                    {Math.round(settings.minCompleteness * 100)}%
                  </span>{" "}
                  of the sliders to enter the matching pool.
                </>
              ) : profile.paused ? (
                "Paused — you won't be matched with anyone until you switch this back on."
              ) : profile.isLive ? (
                "Live. You're in the matching pool."
              ) : (
                <>
                  Goes live in <span className="text-brand">{relativeTime(profile.liveAt)}</span>.
                  Every save restarts this {settings.settleHours}-hour clock.
                </>
              )}
            </p>
          </div>

          <Button
            variant="outline"
            onClick={() => mutate(() => api.post("/api/profile/pause", { paused: !profile.paused }))}
            className="rounded-full text-[13px]"
          >
            {profile.paused ? (
              <>
                <PlayCircle /> Resume matching
              </>
            ) : (
              <>
                <PauseCircle /> Pause matching
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid gap-10 pb-24 lg:grid-cols-[210px_1fr]">
        {/* Section nav.
            min-w-0 is load-bearing: a grid item defaults to min-width:auto, so
            without it this column grows to fit all fourteen tabs and the
            overflow-x-auto below never gets anything to scroll — it just makes
            the whole page 2000px wide instead. */}
        <nav className="min-w-0 lg:sticky lg:top-20 lg:flex lg:max-h-[calc(100vh-6rem)] lg:flex-col lg:self-start">
          {/* The list is the part that scrolls, not the whole column — the
              keyboard legend below stays put. min-h-0 lets it actually shrink
              inside the flex column, and overflow-x can't stay `visible` next
              to a scrolling y (CSS promotes it to auto), so it's pinned hidden
              and the labels truncate instead. */}
          <ul className="flex gap-1.5 overflow-x-auto pb-2 lg:min-h-0 lg:flex-col lg:overflow-x-hidden lg:overflow-y-auto lg:pb-0 lg:pr-1">
            {FIXED_SECTIONS.map((s) => (
              <li key={s.id} className="shrink-0">
                <a
                  href={`#${s.id}`}
                  className={cn(
                    "flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-[13.5px] transition-colors lg:rounded-lg",
                    active === s.id
                      ? "bg-brand/10 font-medium text-brand"
                      : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                  )}
                >
                  <span aria-hidden>{s.emoji}</span>
                  <span className="flex-1 truncate">{s.label}</span>
                </a>
              </li>
            ))}

            <li className="my-1.5 hidden border-t border-border/60 lg:block" />

            {groups.map((g) => {
              const p = groupProgress(g.id);
              const complete = p.total > 0 && p.done === p.total;
              return (
                <li key={g.id} className="shrink-0">
                  <a
                    href={`#${g.key}`}
                    onClick={() => {
                      openGroup(g.key);
                      const first = firstOfGroup.current.get(g.key);
                      if (!first) return;
                      // Land on the first question so ← / → work on arrival —
                      // but a frame later. Following a fragment link focuses
                      // the element it points at, and a <section> isn't
                      // focusable, so the browser resets focus to the body
                      // instead. That happens in this click's default action,
                      // after every handler here has run; anything focused now
                      // is unfocused a moment later.
                      //
                      // Scrolling is left to the anchor for the same reason a
                      // second scroll would fight the first.
                      requestAnimationFrame(() => select(first, { scroll: false }));
                    }}
                    className={cn(
                      "flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-[13.5px] transition-colors lg:rounded-lg",
                      active === g.key
                        ? "bg-brand/10 font-medium text-brand"
                        : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                    )}
                  >
                    <span aria-hidden>{g.emoji}</span>
                    <span className="flex-1 truncate">{g.label}</span>
                    <span
                      className={cn(
                        "hidden text-[11px] tabular-nums lg:inline",
                        complete ? "text-brand" : "text-muted-foreground/60",
                      )}
                    >
                      {complete ? <Check className="size-3" /> : `${p.done}/${p.total}`}
                    </span>
                  </a>
                </li>
              );
            })}
          </ul>

          <KeyboardHints className="mt-4 shrink-0" />
        </nav>

        <div className="min-w-0">
          {/* -- About you ---------------------------------------------------- */}
          <Section
            id="basics"
            emoji="🪪"
            title="About you"
            blurb="Facts, not opinions. Your name and a rough location are the only things a match ever sees written down."
            refs={sectionRefs}
          >
            <Field label="What should a match call you?" required missing={!basics.name.trim()}>
              <Input
                value={basics.name}
                maxLength={LIMITS.name}
                onChange={(e) => patchBasics({ name: e.target.value })}
                placeholder="Sam"
                className="h-11 max-w-sm bg-card/60"
              />
            </Field>

            <Field
              label="Your gender"
              required
              missing={!basics.gender}
              filter
              help="Used as a filter in both directions — theirs against yours, and yours against theirs."
            >
              <SingleChips
                options={GENDERS}
                value={basics.gender}
                onChange={(gender) => patchBasics({ gender })}
              />
            </Field>

            <Field
              label="Date of birth"
              required
              missing={!basics.birthday}
              filter
              help="We store the date, not the age, so your age never goes stale."
            >
              <Input
                type="date"
                value={basics.birthday ?? ""}
                max={new Date(Date.now() - 18 * 365.25 * 864e5).toISOString().slice(0, 10)}
                onChange={(e) => patchBasics({ birthday: e.target.value || null })}
                className="h-11 w-full max-w-[13rem] bg-card/60"
              />
            </Field>

            <Field label="Where you are" required missing={!basics.countryCode}>
              <CountrySelect
                value={basics.countryCode}
                onChange={(countryCode) => patchBasics({ countryCode })}
              />
              <Input
                value={basics.locationText}
                maxLength={LIMITS.locationText}
                onChange={(e) => patchBasics({ locationText: e.target.value })}
                placeholder="City or region — optional"
                className="mt-2.5 h-11 max-w-sm bg-card/60"
              />
            </Field>

            <Field
              label="Languages you speak comfortably"
              required
              missing={basics.languages.length === 0}
              filter
              help="A hard filter: you and a match must share at least one. Nothing else about a match matters if you can't talk to each other."
            >
              <LanguagePicker
                value={basics.languages}
                onChange={(languages) => patchBasics({ languages })}
              />
            </Field>
          </Section>

          {/* -- Filters ------------------------------------------------------ */}
          <Section
            id="filters"
            emoji="🔭"
            title="Who you'll meet"
            blurb="Absolute. Nobody outside these will ever be shown to you at any score, and you won't be shown to them."
            refs={sectionRefs}
          >
            <Field
              label="You want to be matched with"
              required
              missing={basics.seekingGenders.length === 0}
              filter
              help="Pick everything that applies. Applied both ways: they must want your gender too."
            >
              <MultiChips
                options={GENDERS}
                value={basics.seekingGenders}
                onChange={(seekingGenders) => patchBasics({ seekingGenders })}
              />
            </Field>

            <Field
              label="Acceptable age range"
              filter
              help="You must also fall inside theirs, or the pair never comes up."
            >
              <AgeRange min={basics.ageMin} max={basics.ageMax} onChange={patchBasics} />
            </Field>
          </Section>

          {/* -- Words -------------------------------------------------------- */}
          <Section
            id="words"
            emoji="✍️"
            title="Your words"
            blurb="The only part of this profile you write yourself."
            refs={sectionRefs}
          >
            <Field
              label="Public bio"
              help="The only thing a stranger holding your public link can read. Keep it light — this one is genuinely public."
            >
              <Textarea
                value={basics.publicBio}
                maxLength={LIMITS.publicBio}
                onChange={(e) => patchBasics({ publicBio: e.target.value })}
                placeholder="Whatever you'd be happy for anyone to read."
                className="min-h-36 resize-y bg-card/60 text-[15px] leading-relaxed"
              />
            </Field>

            <Field
              label="Your real bio"
              help="Shown only inside a match email, to one person, after the matcher has already paired you. Never on your public link."
            >
              <Textarea
                value={basics.bio}
                maxLength={LIMITS.bio}
                onChange={(e) => patchBasics({ bio: e.target.value })}
                placeholder="Write as much or as little as you like…"
                className="min-h-36 resize-y bg-card/60 text-[15px] leading-relaxed"
              />
            </Field>

            <Field
              label="Contact details"
              help="Released only when you and a match have both pressed the button. Anything you like — email, a phone number, a Signal username."
            >
              <Textarea
                value={basics.contact}
                maxLength={LIMITS.contact}
                onChange={(e) => patchBasics({ contact: e.target.value })}
                placeholder={"signal: sam.42\nreddit: u/samwrites"}
                className="min-h-24 resize-y bg-card/60 text-[15px] leading-relaxed"
              />
            </Field>
          </Section>

          {/* -- Sharing ------------------------------------------------------ */}
          <Section
            id="sharing"
            emoji="🔗"
            title="Public link"
            blurb="Off by default. Turn it on and anyone with the link sees your name, country, languages and short bio — plus, if they're signed in, how well the two of you score. Add a password and whoever you give it to sees your actual answers."
            refs={sectionRefs}
          >
            <Field label="Share link">
              <div className="flex flex-wrap items-center gap-2.5">
                <Button
                  variant={profile.publicEnabled ? "default" : "outline"}
                  onClick={() =>
                    mutate(() =>
                      api.post("/api/profile/public", { enabled: !profile.publicEnabled }),
                    )
                  }
                  className="rounded-full"
                >
                  {profile.publicEnabled ? (
                    <>
                      <Eye /> Link is on
                    </>
                  ) : (
                    <>
                      <EyeOff /> Link is off
                    </>
                  )}
                </Button>

                {profile.publicEnabled && (
                  <>
                    <code className="w-full min-w-0 flex-1 basis-full truncate rounded-full border border-border/70 bg-card/60 px-4 py-2 text-[13px] sm:basis-auto">
                      {publicUrl}
                    </code>
                    <Button
                      variant="outline"
                      size="icon"
                      title="Copy link"
                      className="rounded-full"
                      onClick={() => {
                        navigator.clipboard.writeText(publicUrl);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1800);
                      }}
                    >
                      {copied ? <Check className="text-brand" /> : <Copy />}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      title="Issue a new link, retiring the old one"
                      className="rounded-full"
                      onClick={() => mutate(() => api.post("/api/profile/public/rotate"))}
                    >
                      <RefreshCw />
                    </Button>
                  </>
                )}
              </div>
              {profile.publicEnabled && (
                <p className="mt-3 text-[13px] leading-relaxed text-muted-foreground">
                  Without a password below, that page shows percentages and nothing else.{" "}
                  <Link
                    href={`/u/${profile.slug}`}
                    className="underline underline-offset-4 hover:text-foreground"
                  >
                    See what it looks like
                  </Link>
                  .
                </p>
              )}
            </Field>

            {profile.publicEnabled && (
              <AnswerPasswordField profile={profile} groups={groups} mutate={mutate} />
            )}
          </Section>

          {/* -- The sliders --------------------------------------------------- */}
          {groups.map((g) => {
            const qs = questionsByGroup.get(g.id) ?? [];
            if (qs.length === 0) return null;
            const p = groupProgress(g.id);
            return (
              <GroupSection
                key={g.id}
                group={g}
                questions={qs}
                answers={answers}
                activeId={activeId}
                promptById={promptById}
                done={p.done}
                open={openGroups.has(g.key)}
                refs={sectionRefs}
                onToggle={toggleGroup}
                onChange={setAnswer}
                onImportance={setImportance}
                onClear={clearAnswer}
                onActivate={activateRow}
                onRegister={register}
              />
            );
          })}

          {/* -- Danger -------------------------------------------------------- */}
          {/* mt so this keeps its distance from a closed area's lid too; against
              an open one the two margins collapse and nothing moves. */}
          <section className="mt-10 rounded-2xl border border-destructive/25 bg-destructive/5 p-5">
            <h2 className="text-[15px] font-semibold">Delete everything</h2>
            <p className="mt-1.5 max-w-xl text-[14px] leading-relaxed text-muted-foreground">
              Removes your profile, every answer and any matches you're part of. Immediate and
              permanent — there's no copy to restore from.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {confirmDelete ? (
                <>
                  <Button
                    variant="destructive"
                    className="rounded-full"
                    onClick={async () => {
                      await api.del("/api/profile");
                      window.location.href = "/";
                    }}
                  >
                    <Trash2 /> Yes, delete it all
                  </Button>
                  <Button
                    variant="ghost"
                    className="rounded-full"
                    onClick={() => setConfirmDelete(false)}
                  >
                    Keep my profile
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Trash2 /> Delete my profile
                  </Button>
                  <Button
                    variant="ghost"
                    className="rounded-full text-muted-foreground"
                    onClick={async () => {
                      await api.post("/api/auth/logout");
                      navigate("/");
                    }}
                  >
                    Sign out on this device
                  </Button>
                </>
              )}
            </div>
          </section>
        </div>
      </div>

    </Shell>
  );
}

// ---------------------------------------------------------------------------

/**
 * Save state and the Save button, living in the app bar.
 *
 * This used to be a bar pinned to the bottom of the viewport, which was a
 * mistake once keyboard navigation existed: pressing ↓ scrolls the next
 * question just into view, and "just into view" was underneath the bar. The
 * header is already sticky and already reserves its space, so nothing can
 * hide behind it.
 *
 * The label collapses before the button does — at 320px only the button and
 * a state dot survive, which is the part you actually need.
 */
const SaveControls = memo(function SaveControls({
  state,
  settleHours,
  liveAt,
  paused,
  isLive,
  onSave,
}: {
  state: SaveState;
  settleHours: number;
  liveAt: string;
  paused: boolean;
  isLive: boolean;
  onSave: () => void;
}) {
  const detail =
    state === "dirty"
      ? `Unsaved changes. Saving restarts the ${settleHours}-hour clock.`
      : state === "error"
        ? "Couldn't save. Try again."
        : state === "saved"
          ? `Saved — live in ${relativeTime(liveAt)}`
          : state === "saving"
            ? "Saving…"
            : paused
              ? "Paused — not being matched"
              : isLive
                ? "Live in the matching pool"
                : `Goes live in ${relativeTime(liveAt)}`;

  const short =
    state === "dirty"
      ? "Unsaved"
      : state === "error"
        ? "Failed"
        : state === "saved"
          ? "Saved"
          : state === "saving"
            ? "Saving…"
            : paused
              ? "Paused"
              : isLive
                ? "Live"
                : "Settling";

  const icon =
    state === "saving" ? (
      <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
    ) : state === "saved" ? (
      <Check className="size-3.5 shrink-0 text-brand" />
    ) : state === "dirty" ? (
      <CircleDot className="size-3.5 shrink-0 text-brand" />
    ) : state === "error" ? (
      <AlertTriangle className="size-3.5 shrink-0 text-destructive" />
    ) : (
      <Clock className="size-3.5 shrink-0 text-muted-foreground" />
    );

  return (
    <span className="flex items-center gap-2" title={detail}>
      {icon}
      <span
        className={cn(
          "hidden whitespace-nowrap text-[13px] md:inline",
          state === "error" ? "text-destructive" : "text-muted-foreground",
        )}
      >
        {/* The full sentence only where there's room for it. */}
        <span className="hidden lg:inline">{detail}</span>
        <span className="lg:hidden">{short}</span>
      </span>
      <Button
        onClick={onSave}
        disabled={state === "saving" || state === "clean" || state === "saved"}
        size="sm"
        className="shrink-0 rounded-full px-4"
      >
        Save
      </Button>
    </span>
  );
});

/**
 * One group of sliders.
 *
 * The custom comparator is the point of this component. `answers` is a single
 * object, so every keystroke gives it a new identity and a stock `memo` would
 * never skip anything — all eleven sections would rebuild their ~140 child
 * elements for one slider moving. Comparing only the answers belonging to
 * *this* group means editing "Movement" leaves the other ten sections
 * completely untouched, and the memo on ScaleSlider then narrows it further to
 * the single row that actually changed.
 *
 * The comparator is safe because the props are exhaustively covered: group and
 * questions by identity, activeId by membership, answers by key, and every
 * callback is stable for the page's lifetime.
 */
const GroupSection = memo(
  function GroupSection({
    group,
    questions,
    answers,
    activeId,
    promptById,
    done,
    open,
    refs,
    onToggle,
    onChange,
    onImportance,
    onClear,
    onActivate,
    onRegister,
  }: {
    group: Group;
    questions: Question[];
    answers: Answers;
    activeId: string | null;
    /** Whole-catalogue lookup: a paired question can live in another group. */
    promptById: Map<string, string>;
    done: number;
    open: boolean;
    refs: React.RefObject<Map<string, HTMLElement>>;
    onToggle: (groupKey: string) => void;
    onChange: (questionId: string, value: ScaleValue) => void;
    onImportance: (questionId: string, importance: Importance) => void;
    onClear: (questionId: string) => void;
    onActivate: (questionId: string) => void;
    onRegister: (questionId: string, el: HTMLInputElement | null) => void;
  }) {
    return (
      <Section
        id={group.key}
        emoji={group.emoji}
        title={group.label}
        blurb={group.blurb}
        count={`${done}/${questions.length}`}
        refs={refs}
        rowsSelfPad
        open={open}
        onToggle={() => onToggle(group.key)}
      >
        {/* Guarded rather than left to Section: building a closed area's rows
            only to throw them away is the whole cost of the page. */}
        {open &&
          questions.map((q) => (
            <ScaleSlider
              key={q.id}
              question={q}
              value={answers[q.id]?.value}
              importance={answers[q.id]?.importance ?? 0}
              active={activeId === q.id}
              linkedPrompt={
                q.linkedQuestionId ? (promptById.get(q.linkedQuestionId) ?? null) : null
              }
              onChange={onChange}
              onImportance={onImportance}
              onClear={onClear}
              onActivate={onActivate}
              onRegister={onRegister}
            />
          ))}
      </Section>
    );
  },
  (prev, next) => {
    if (
      prev.group !== next.group ||
      prev.questions !== next.questions ||
      // Derived from the same catalogue as `questions`, so this can only
      // differ when that did — checked anyway, so the claim above stays true.
      prev.promptById !== next.promptById ||
      prev.done !== next.done ||
      prev.open !== next.open
    ) {
      return false;
    }

    // A closed area draws no rows, so nothing below can change what it looks
    // like — and the answers it holds are still saved from the page's state.
    if (!next.open) return true;

    // The highlight only concerns the section it moved into or out of.
    if (prev.activeId !== next.activeId) {
      const touchesThisGroup = next.questions.some(
        (q) => q.id === prev.activeId || q.id === next.activeId,
      );
      if (touchesThisGroup) return false;
    }

    for (const q of next.questions) {
      if (prev.answers[q.id] !== next.answers[q.id]) return false;
    }
    return true;
  },
);

/**
 * The password that opens your answers to whoever you give it to.
 *
 * Shown back to you in the clear, because a secret you hand out by hand is
 * useless if you can't look it up in three months and tell somebody what it is.
 * That is a deliberate trade against hashing it, and it means this string is
 * only as safe as the database — which is why the copy below says what it opens
 * rather than leaving that to be discovered.
 *
 * The checkbox names the areas it would open by name, resolved from the live
 * catalogue rather than hard-coded, so nobody agrees to "the private areas" in
 * the abstract and finds out later which ones those were.
 */
function AnswerPasswordField({
  profile,
  groups,
  mutate,
}: {
  profile: ProfileT;
  groups: Group[];
  mutate: (fn: () => Promise<{ profile: ProfileT }>) => Promise<void>;
}) {
  const [draft, setDraft] = useState(profile.publicPassword);
  const [includePrivate, setIncludePrivate] = useState(profile.publicPasswordPrivate);
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const privateGroups = groups.filter((g) => !g.publicBreakdown);
  const dirty =
    draft.trim() !== profile.publicPassword ||
    (draft.trim() !== "" && includePrivate !== profile.publicPasswordPrivate);

  async function save(password: string, include: boolean) {
    setBusy(true);
    setError(null);
    try {
      await mutate(() =>
        api.post("/api/profile/public/password", { password, includePrivate: include }),
      );
      setDraft(password);
      setIncludePrivate(password === "" ? false : include);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save that.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Field
      label="Answer password"
      help="Optional, and separate from the link. Anyone signed in who has both can see your answers next to their own — but only on questions they have answered too, so nobody reads yours without having given theirs first."
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <Input
          type={reveal ? "text" : "password"}
          value={draft}
          autoComplete="off"
          maxLength={LIMITS.publicPassword}
          placeholder="No password — answers stay hidden"
          onChange={(e) => setDraft(e.target.value)}
          className="min-w-0 flex-1 basis-48 bg-card/60 text-[15px]"
        />
        <Button
          variant="outline"
          size="icon"
          title={reveal ? "Hide" : "Show"}
          className="rounded-full"
          onClick={() => setReveal(!reveal)}
        >
          {reveal ? <EyeOff /> : <Eye />}
        </Button>
        {profile.publicPassword && (
          <Button
            variant="outline"
            size="icon"
            title="Copy password"
            className="rounded-full"
            onClick={() => navigator.clipboard.writeText(profile.publicPassword)}
          >
            <Copy />
          </Button>
        )}
        <Button
          disabled={busy || !dirty}
          className="rounded-full px-5"
          onClick={() => save(draft.trim(), includePrivate)}
        >
          {busy ? <Loader2 className="animate-spin" /> : saved ? <Check /> : null}
          {draft.trim() === "" && profile.publicPassword ? "Remove" : "Save"}
        </Button>
      </div>

      {privateGroups.length > 0 && (
        <label className="mt-3.5 flex cursor-pointer items-start gap-2.5 text-[13px] leading-relaxed">
          <input
            type="checkbox"
            checked={includePrivate}
            onChange={(e) => setIncludePrivate(e.target.checked)}
            className="mt-0.5 size-4 shrink-0 accent-brand"
          />
          <span className="text-muted-foreground">
            Also open the areas kept off the public page —{" "}
            {privateGroups.map((g, i) => (
              <span key={g.id} className="text-foreground">
                {i > 0 && ", "}
                <span aria-hidden>{g.emoji}</span> {g.label}
              </span>
            ))}
            . Without this, the password opens everything except those; they still count towards
            the percentages either way.
          </span>
        </label>
      )}

      <p className="mt-3 text-[12.5px] leading-relaxed text-muted-foreground">
        {profile.publicPassword ? (
          <>
            Set{profile.publicPasswordPrivate ? ", including the private areas" : ""}. Changing it
            here shuts out anyone still using the old one. Whoever has it must type it again after
            every reload — nothing about opening your page is remembered.
          </>
        ) : (
          <>
            No password set. Anyone signed in sees percentages by area and nothing more.
          </>
        )}
      </p>

      {error && <p className="mt-2 text-[12.5px] text-destructive">{error}</p>}
    </Field>
  );
}

/**
 * One titled block of the page.
 *
 * Passing `onToggle` turns the heading into a disclosure and hands the open
 * state to the caller; without one the section renders exactly as it always
 * has, which is what the four fixed sections at the top want — they're short,
 * they hold the required fields, and a new profile shouldn't open on four
 * closed lids.
 */
function Section({
  id,
  emoji,
  title,
  blurb,
  count,
  refs,
  rowsSelfPad = false,
  open,
  onToggle,
  children,
}: {
  id: string;
  emoji: string;
  title: string;
  blurb: string;
  count?: string;
  refs: React.RefObject<Map<string, HTMLElement>>;
  /**
   * True when the children already carry their own vertical padding. Slider
   * rows do (they need it for the active-row highlight to have height); Field
   * rows deliberately zero their first and last, expecting the card to supply
   * it. Without the distinction one of the two always looks wrong.
   */
  rowsSelfPad?: boolean;
  open?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}) {
  const expanded = onToggle ? Boolean(open) : true;

  const head = (
    <>
      {onToggle && (
        <ChevronRight
          aria-hidden
          className={cn(
            "size-4 shrink-0 text-muted-foreground/60 transition-transform",
            expanded && "rotate-90",
          )}
        />
      )}
      <span className="text-xl" aria-hidden>
        {emoji}
      </span>
      {/* min-w-0 so a long title wraps inside the row rather than pushing the
          count off the end of a narrow screen. */}
      <span className="min-w-0 text-[22px] font-semibold tracking-[-0.02em]">{title}</span>
      {count && (
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11.5px] tabular-nums text-muted-foreground">
          {count}
        </span>
      )}
    </>
  );

  return (
    <section
      id={id}
      ref={(el) => {
        if (el) refs.current.set(id, el);
      }}
      // A closed section is a single row; twelve of them 48px apart read as a
      // list of unrelated pages rather than one contents.
      className={cn("scroll-mt-20", expanded ? "mb-12" : "mb-2")}
    >
      <h2 className="mb-1">
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            // -mx/px so the hover tint bleeds past the text rather than sitting
            // inset from the column it shares with the cards below.
            className="-mx-2.5 flex w-full items-center gap-2.5 rounded-xl px-2.5 py-1.5 text-left transition-colors hover:bg-accent/50"
          >
            {head}
          </button>
        ) : (
          <span className="flex items-center gap-2.5">{head}</span>
        )}
      </h2>

      {expanded && (
        <>
          <p className="mb-3 text-[14px] leading-relaxed text-muted-foreground">{blurb}</p>
          <div
            className={cn(
              "rounded-2xl border border-border/70 bg-card/40 px-5 backdrop-blur-sm sm:px-6",
              // Either way the inner content ends up ~20px from every edge, so
              // the card reads as evenly padded whichever kind of row it holds.
              rowsSelfPad ? "py-1.5" : "py-5 sm:py-6",
            )}
          >
            {children}
          </div>
        </>
      )}
    </section>
  );
}

function extractBasics(p: ProfileT): Basics {
  return {
    name: p.name,
    gender: p.gender,
    birthday: p.birthday,
    countryCode: p.countryCode,
    locationText: p.locationText,
    languages: p.languages,
    seekingGenders: p.seekingGenders,
    ageMin: p.ageMin,
    ageMax: p.ageMax,
    publicBio: p.publicBio,
    bio: p.bio,
    contact: p.contact,
  };
}
